import { type FC, type ReactNode, useEffect, useMemo, useState } from "react";

import type { FileRejection } from "react-dropzone";

import { Text, toastError } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetOwnerDocuments,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  deleteFileUpload,
  postFileUpload,
} from "~/v2/api/rest/entityOnboarding";
import {
  type ExistingFile,
  FILE_ACCEPTED_TYPES,
  FILE_MAX_SIZE,
  FILE_SIZE_LABEL,
  FileDropzone,
} from "~/v2/components/fileUpload";
import { normalizeDocType } from "~/v2/utils/normalizeDocType";
import { REQUIRED_ERR_MSG } from "../../constants";

export type DocumentUploadProps = {
  accountId: string;
  ownerId: string;
  onChange: (isUploaded: boolean) => void;
  isRequiredErrorDisplayed: boolean;
  docType: string;
  docSubType?: string;
  label: string;
  description: ReactNode;
  name: string;
};

// Used for immediate file uploads to owner documents endpoint
export const DocumentUpload: FC<DocumentUploadProps> = ({
  accountId,
  ownerId,
  onChange,
  isRequiredErrorDisplayed,
  docType,
  docSubType,
  label,
  description,
  name,
}) => {
  const { documents: ownerDocuments, refetch } = useGetOwnerDocuments({
    accountId,
    ownerId,
    options: { enabled: !!ownerId },
  });

  const existingDoc = useMemo(
    () =>
      ownerDocuments?.find(
        (doc) =>
          normalizeDocType(doc.doc_type) === docType &&
          (docSubType ? doc.doc_sub_type === docSubType : true),
      ),
    [ownerDocuments, docType, docSubType],
  );

  const [state, setState] = useState<{
    file: File | ExistingFile | null;
    error: string | null;
    isLoading: boolean;
  }>({
    file: null,
    error: null,
    isLoading: false,
  });

  const uploadMutation = useMutationRequest(
    (file: File) =>
      postFileUpload({
        accountId,
        ownerId,
        docType,
        docSubType,
        file,
      }),
    {
      onSuccess: () => {
        refetch();
        onChange(true);
        setState((prevState) => ({ ...prevState, isLoading: false }));
      },
      onError: (error) => {
        const errorMsg = getErrorMessage(error);
        toastError(`Error uploading file: ${errorMsg}`);
        setState({
          file: null,
          error: errorMsg,
          isLoading: false,
        });
      },
    },
  );

  const deleteMutation = useMutationRequest(
    (fileId: string) =>
      deleteFileUpload({
        accountId,
        ownerId,
        documentId: fileId,
      }),
    {
      onSuccess: () => {
        onChange(false);
        setState({
          file: null,
          error: null,
          isLoading: false,
        });
        refetch();
      },
      onError: (error) => {
        const errorMsg = getErrorMessage(error);
        toastError(`Error deleting file: ${errorMsg}`);
        setState((prevState) => ({
          ...prevState,
          error: errorMsg,
          isLoading: false,
        }));
      },
    },
  );

  const onUploadFile = (file: File) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
    }));
    uploadMutation.mutate(file);
  };

  const onUploadError = (fileRejection: FileRejection) => {
    setState({
      file: null,
      error: fileRejection.errors[0].message,
      isLoading: false,
    });

    onChange(false);
  };

  const onRemoveFile = () => {
    if (state.file == null || !Object.hasOwn(state.file, "id")) {
      toastError("No file to remove");
      return;
    }
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    const fileId = (state.file as ExistingFile).id;
    deleteMutation.mutate(fileId);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: onChange is a caller-supplied callback, normally an inline arrow, so listing it would loop: the effect calls onChange(true), the parent re-renders with a new onChange, and the effect fires again. existingDoc is the real trigger.
  useEffect(() => {
    if (existingDoc) {
      setState((prevState) => ({
        ...prevState,
        file: {
          id: existingDoc.id,
          name: existingDoc.doc_type,
        },
      }));
      onChange(true);
    }
  }, [existingDoc]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label htmlFor={name} className="block font-medium text-sm">
          {label}
        </label>
        {(isRequiredErrorDisplayed || state.error) && (
          <Text className="truncate text-rose-500 text-xs">
            {isRequiredErrorDisplayed ? REQUIRED_ERR_MSG : state.error}
          </Text>
        )}
      </div>

      <Text className="text-gray-600 text-sm">{description}</Text>
      <FileDropzone
        name={name}
        label={label}
        file={state.file}
        placeholder={name}
        onUpload={onUploadFile}
        onError={onUploadError}
        onRemove={onRemoveFile}
        isRequired
        maxSize={FILE_MAX_SIZE}
        maxSizeLabel={FILE_SIZE_LABEL}
        accept={FILE_ACCEPTED_TYPES}
        displayType="file"
        isLoading={uploadMutation.isPending || deleteMutation.isPending}
      />
    </div>
  );
};
